import React, { useEffect, useState } from 'react';
import { Modal } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ProductFilters, FILTER_FACET_KEYS } from './productFilters';

interface Props {
  open: boolean;
  filters: ProductFilters;
  columnCount: number;
  saving: boolean;
  onClose: () => void;
  onSave: (_name: string) => void;
}

const FACET_LABELS: Record<string, string> = {
  status: 'Compliance',
  jurisdiction: 'Jurisdiction',
  tier: 'Tier',
  origin: 'Origin',
  destination: 'Destination',
  htsChapter: 'HTS Chapter',
};

const SaveViewModal: React.FC<Props> = ({
  open,
  filters,
  columnCount,
  saving,
  onClose,
  onSave,
}) => {
  const [name, setName] = useState('');

  useEffect(() => {
    if (open) setName('');
  }, [open]);

  const summary = FILTER_FACET_KEYS.filter((k) => filters[k].length > 0).map(
    (k) => ({ label: FACET_LABELS[k], count: filters[k].length }),
  );

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} className='eh-modal-root'>
      <div className='eh-modal' role='dialog' aria-label='Save view'>
        <div className='eh-modal-head'>
          <div>
            <h2 className='eh-modal-title'>Save current view</h2>
            <p className='eh-modal-subtitle'>
              Save these filters and columns to reuse later.
            </p>
          </div>
          <button
            className='eh-modal-close'
            onClick={handleClose}
            aria-label='Close'
            type='button'
          >
            <CloseIcon style={{ fontSize: 20 }} />
          </button>
        </div>

        <div className='eh-modal-body'>
          <label className='eh-field'>
            <span className='eh-field-label'>
              View name <span className='eh-req'>*</span>
            </span>
            <input
              className='eh-input'
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder='e.g. ITAR — high risk'
              maxLength={120}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && name.trim() && !saving) onSave(name.trim());
              }}
            />
          </label>

          <div className='eh-field'>
            <span className='eh-field-label'>Captured in this view</span>
            <div className='eh-summary-chips'>
              {summary.length === 0 && (
                <span className='eh-summary-empty'>No active filters</span>
              )}
              {summary.map((s) => (
                <span key={s.label} className='eh-summary-chip'>
                  {s.label}
                  <span className='eh-summary-count'>{s.count}</span>
                </span>
              ))}
              <span className='eh-summary-chip'>
                Columns
                <span className='eh-summary-count'>{columnCount}</span>
              </span>
            </div>
          </div>
        </div>

        <div className='eh-modal-foot'>
          <button
            className='btn-outline'
            type='button'
            onClick={handleClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            className='btn-primary'
            type='button'
            onClick={() => onSave(name.trim())}
            disabled={saving || !name.trim()}
            style={{ opacity: saving || !name.trim() ? 0.6 : 1 }}
          >
            {saving ? 'Saving…' : 'Save view'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default SaveViewModal;
